import React, { useEffect, useState } from 'react';
import { ClaimedLoanView } from '../components/ClaimedLoanView';
import { RetentionActionModal } from '../components/RetentionActionModal';
import { Loan, RetentionOutcome } from '../types';
import { Button } from '../components/ui/Button';
import { AnimatePresence, motion } from 'framer-motion';
// MOCK DATA GENERATION
const generateMockData = () => {
  const activeLoans: Loan[] = [{
    id: '1',
    loanNumber: 'LN-2024-83921',
    borrowerName: 'Sarah Jenkins',
    borrowerPhone: '(512) 555-0147',
    loanOfficer: 'Michael Scott',
    pushedBy: 'Dwight Schrute',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    retentionComment: 'Borrower unresponsive - 3 attempts made',
    status: 'Active',
    amount: 450000
  }, {
    id: '2',
    loanNumber: 'LN-2024-83925',
    borrowerName: 'Robert California',
    borrowerPhone: '(310) 555-0293',
    loanOfficer: 'Jim Halpert',
    pushedBy: 'Andy Bernard',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
    retentionComment: 'Rate lock expired - borrower reconsidering',
    status: 'Active',
    amount: 620000
  }, {
    id: '3',
    loanNumber: 'LN-2024-83930',
    borrowerName: 'Stanley Hudson',
    borrowerPhone: '(215) 555-0184',
    loanOfficer: 'Phyllis Vance',
    pushedBy: 'Angela Martin',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
    retentionComment: 'Missing income documentation for co-borrower',
    status: 'Active',
    amount: 325000
  }, {
    id: '4',
    loanNumber: 'LN-2024-83942',
    borrowerName: 'Kelly Kapoor',
    borrowerPhone: '(469) 555-0321',
    loanOfficer: 'Ryan Howard',
    pushedBy: 'Toby Flenderson',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
    retentionComment: 'Appraisal came in low - contesting value',
    status: 'Active',
    amount: 290000
  }, {
    id: '5',
    loanNumber: 'LN-2024-83955',
    borrowerName: 'Kevin Malone',
    borrowerPhone: '(602) 555-0178',
    loanOfficer: 'Oscar Martinez',
    pushedBy: 'Angela Martin',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
    retentionComment: 'DTI ratio exceeds guidelines after credit refresh',
    status: 'Active',
    amount: 410000
  }, {
    id: '6',
    loanNumber: 'LN-2024-83961',
    borrowerName: 'Meredith Palmer',
    borrowerPhone: '(813) 555-0256',
    loanOfficer: 'Creed Bratton',
    pushedBy: 'Pam Beesly',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8).toISOString(),
    retentionComment: 'Title issues discovered on property history',
    status: 'Active',
    amount: 185000
  }, {
    id: '7',
    loanNumber: 'LN-2024-83977',
    borrowerName: 'Erin Hannon',
    borrowerPhone: '(720) 555-0199',
    loanOfficer: 'Andy Bernard',
    pushedBy: 'Gabe Lewis',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 0.5).toISOString(),
    retentionComment: 'Employment verification failed',
    status: 'Active',
    amount: 340000
  }, {
    id: '8',
    loanNumber: 'LN-2024-83989',
    borrowerName: 'Darryl Philbin',
    borrowerPhone: '(404) 555-0342',
    loanOfficer: 'Jim Halpert',
    pushedBy: 'Val Johnson',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    retentionComment: 'Borrower requested hold due to family emergency',
    status: 'Active',
    amount: 515000
  }];
  return {
    activeLoans
  };
};
const CURRENT_USER = 'Jane Cooper';
export function RetentionCenter() {
  const [data, setData] = useState(generateMockData());
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastReleasedId, setLastReleasedId] = useState<string | null>(null);
  // Auto-release expired claims (older than 1 hour)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;
      setData(prev => {
        let hasChanges = false;
        const updatedLoans = prev.activeLoans.map(loan => {
          if (loan.claimedAt) {
            const claimTime = new Date(loan.claimedAt).getTime();
            if (now - claimTime >= oneHour) {
              hasChanges = true;
              return {
                ...loan,
                claimedBy: undefined,
                claimedAt: undefined
              };
            }
          }
          return loan;
        });
        return hasChanges ? {
          ...prev,
          activeLoans: updatedLoans
        } : prev;
      });
    }, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);
  const claimedLoan = data.activeLoans.find(loan => loan.claimedBy === CURRENT_USER);
  const unclaimedCount = data.activeLoans.filter(loan => !loan.claimedBy).length;
  const handleClaimNext = () => {
    const nextLoan = data.activeLoans.find(loan => !loan.claimedBy && loan.id !== lastReleasedId);
    if (nextLoan) {
      setData(prev => ({
        ...prev,
        activeLoans: prev.activeLoans.map(loan => loan.id === nextLoan.id ? {
          ...loan,
          claimedBy: CURRENT_USER,
          claimedAt: new Date().toISOString()
        } : loan)
      }));
    }
  };
  const handleRelease = (loanId: string) => {
    setLastReleasedId(loanId);
    setData(prev => ({
      ...prev,
      activeLoans: prev.activeLoans.map(loan => loan.id === loanId ? {
        ...loan,
        claimedBy: undefined,
        claimedAt: undefined
      } : loan)
    }));
  };
  const handleActionClick = (loan: Loan) => {
    if (loan.claimedBy === CURRENT_USER) {
      setSelectedLoan(loan);
      setIsModalOpen(true);
    }
  };
  const handleActionComplete = (loanId: string, outcome: RetentionOutcome, notes: string) => {
    setData(prev => ({
      ...prev,
      activeLoans: prev.activeLoans.filter(l => l.id !== loanId)
    }));
  };
  return <div className="min-h-screen bg-slate-100 font-sans text-slate-900" data-id="element-160">
      {/* Header Section */}
      <div className="bg-accent-50/50 border-b border-accent-100/50 px-8 py-10" data-id="element-161">
        <div className="max-w-5xl mx-auto" data-id="element-162">
          <span className="text-xs font-bold text-accent-700 uppercase tracking-wider mb-2 block" data-id="element-163">
            Retention Center
          </span>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-164">
            Claim Next Retention Loan
          </h1>
          <p className="text-slate-600 mb-6" data-id="element-165">
            Claim one retention loan at a time, work that file, then release it
            back to the bottom of the queue.
          </p>
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-accent-100/50 text-accent-800 text-sm font-medium border border-accent-200/50" data-id="element-166">
            {unclaimedCount} unclaimed retention loans remaining.
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-8 py-8" data-id="element-167">
        <AnimatePresence mode="wait" data-id="element-168">
          {claimedLoan ? <ClaimedLoanView key="claimed" loan={claimedLoan} onRelease={handleRelease} onOpenLoan={handleActionClick} onAction={handleActionComplete} data-id="element-169" /> : <motion.div key="empty" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center flex flex-col items-center justify-center min-h-[300px]" data-id="element-170">
              <div className="w-16 h-16 bg-accent-50 rounded-full flex items-center justify-center mb-4" data-id="element-171">
                <svg className="w-8 h-8 text-accent-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-172">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" data-id="element-173" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-174">
                Ready to work?
              </h3>
              <p className="text-slate-500 mb-6 max-w-sm" data-id="element-175">
                There are {unclaimedCount} loans waiting in the retention queue.
                Claim the next one to get started.
              </p>
              <Button size="lg" onClick={handleClaimNext} disabled={unclaimedCount === 0} className="bg-accent-600 hover:bg-accent-700 text-white" data-id="element-176">
                Claim Next Loan
              </Button>
            </motion.div>}
        </AnimatePresence>
      </main>

      <RetentionActionModal loan={selectedLoan} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAction={handleActionComplete} data-id="element-177" />
    </div>;
}